import React, { FC, useCallback, useEffect, useRef, useState } from "react"
import { observer } from "mobx-react-lite"
import { Appearance, ImageBackground, ImageStyle, ViewStyle } from "react-native"
import {
  AutoImage, Button, FlatList, ListRenderItem, ListRenderItemInfo,
  // Screen,
  Text,
} from "../../../../components"
import { color, spacing } from "../../../../theme"
import { useStores } from "../../../../models"
import { View } from "native-base"
import { translate } from "../../../../i18n"
import useWindowDimensions from "../../../../utils/use-window-dimensions"

const pattern = require("./pattern2.png")

// const ROOT: ViewStyle = {
//   flex: 1,
// }

const IMAGE: ImageStyle = {
  flex: 1,
  justifyContent: "center",
  borderRadius: 10,
  overflow: "hidden",
}

const CARD: ViewStyle = {
  backgroundColor: 'white',
  borderRadius: 16,
  alignItems: 'center',
  shadowColor: color.primary,
  shadowOffset: {
    width: 0,
    height: 2,
  },
  shadowOpacity: 1,
  shadowRadius: 4,
  elevation: 5,
}

const BUTTON: ViewStyle = {
  flex: 1
}

const BUTTON_ANSWER: ViewStyle = {
  marginVertical: 10,
  paddingVertical: spacing[2],
  paddingHorizontal: spacing[2],
  borderRadius: 4,
  alignItems: "center",
}

const IMAGE_ICON: ImageStyle = {
  width: 20,
  height: 20,
  borderRadius: 20 / 2,
  backgroundColor: color.palette.white
}

const PREV_BUTTON: ViewStyle = {
  ...BUTTON,
  backgroundColor: color.primary,
  opacity: 0.6
}

const Item: FC<{ colorScheme?: "light" | "dark", info: ListRenderItemInfo<any>, data?: Array<any>, onDone?: () => void, handleButton?: (value) => void}> = (props) => {
  const { colorScheme = "light" || Appearance.getColorScheme(), info, data = [], handleButton, onDone } = props;
  const { item: { answerKey, fields: { question, answers, description, key } }, index } = info;

  const right = require("./right.png")
  const wrong = require("./wrong.png")

  const last = index === 9;

  const dimension = useWindowDimensions()

  const onClick = useCallback((value) => {
    handleButton(value)
  },[handleButton])

  return (
    <View minHeight={dimension.height - 80} width={dimension.width - 32} justifyContent="space-between" backgroundColor="transparent">
      <View flex={1} justifyContent="center" px="20px">
        <View
          alignItems="center"
          backgroundColor={color.palette.white}
          style={CARD}
          borderWidth="1px"
          borderColor={color.palette.white}
          mx="16px"
          px="12px"
          py="30px"
          borderRadius="6px"
          position="relative"
        >
          <View position="absolute" flexDirection="row" alignItems="center" py="8px" px="19px" top="-20px" backgroundColor={color.primary} borderRadius="4px">
            <View width="8px" height="8px" mr="7px" borderRadius="4px" backgroundColor={color.palette.white} />
            <Text preset="title3" text={translate("startQuizScreen.questionLength", { index: `${info.index + 1}`})} color={color.palette.white} colorScheme={colorScheme} />
            <View width="8px" height="8px" ml="7px" borderRadius="4px" backgroundColor={color.palette.white} />
          </View>
          <Text preset="title2" textAlign="center" px="16px" text={question} color={color.primary} colorScheme={colorScheme} />
        </View>
        {/* DESCRIPTION */}
        <View alignItems="center" mt="20.64px">
          <Text preset="title3" tx="startQuizScreen.description" colorScheme={colorScheme} />
          <Text preset="bottomLabel2" mt="7px" textAlign="center" text={description} color={color.palette.gray800} colorScheme={colorScheme} />
        </View>
        <View mt="30px">
          {answerKey !== key && <View flexDirection="row" backgroundColor={color.primary} style={BUTTON_ANSWER}>
            <AutoImage source={wrong} style={IMAGE_ICON} />
            <Text preset="title3" text={answers[answerKey]} ml="11.34px" color={color.palette.white} colorScheme={colorScheme} />
          </View>}
          <View
            borderWidth={1}
            borderColor={color.primary}
            backgroundColor={answerKey !== key ? color.palette.white : color.primary}
            flexDirection="row"
            style={BUTTON_ANSWER}>
            <AutoImage source={right} style={IMAGE_ICON} />
            <Text preset="title3" text={answers[key]} ml="11.34px" color={answerKey !== key ? color.primary : color.palette.white} colorScheme={colorScheme} />
          </View>
        </View>
      </View>
      <View mx="18px" py="16px" flexDirection="row">
          <Button
            testID="jala:id/quiz-description-previous-button"
            tx="homeScreen.tooltipPrevious"
            style={index !== 0 ? [PREV_BUTTON, {opacity: 1}] : PREV_BUTTON}
            // textColor={index === 0 ? color.palette.gray400 : color.palette.white}
            disabled={index === 0}
            colorScheme={colorScheme}
            onPress={() => onClick(false)}
          />
          <View width="16px" />
          <Button
            testID="jala:id/quiz-description-next-button"
            tx={!last ? `common.next`: `common.done`}
            style={[BUTTON, { backgroundColor: data.length === index && index !== (data.length - 1) ? color.palette.gray500 : color.palette.orange500}]}
            colorScheme={colorScheme}
            onPress={last ? onDone : () => onClick(true)}
          />
        </View>
    </View>
  )
}

export const QuestionWithAnswerDetailScreen: FC<{ colorScheme?: "light" | "dark", scrollRef?: any }> = observer(function QuestionWithAnswerDetailScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), scrollRef } = props

  const [ index, setIndex ] = useState<number>(0);
  const [ result, setResult ] = useState<Array<any>>([]);

  const FlatListRef = useRef<FlatList<any>>()

  const dimension = useWindowDimensions()

  // Pull in one of our MST stores
  const { quizStore: { questions, answers } } = useStores();

  useEffect(() => {
    const output = questions.map(question => ({...question,  ...answers.find(answer => question.fields.id === answer.id)}));
    setResult(output);
  }, [answers, questions])

  const handleButton = useCallback((next: boolean) => {
    if (next) {
      if (result.length > index) setIndex(index + 1);
      FlatListRef.current.scrollToOffset({ offset: (dimension.width - 32) * (index + 1) })
    }
    else {
      if (result.length > 1) setIndex(index - 1);
      FlatListRef.current.scrollToOffset({ offset: (dimension.width - 32) * (index - 1) })
    }
  }, [dimension.width, index, result.length])

  const handleDone = useCallback(() => {
    scrollRef.current?.scrollTo({
      y: 0,
      animated: true,
    });
    setIndex(0)
    FlatListRef.current.scrollToIndex({ index: 0 });
  }, [scrollRef])

  const renderItem: ListRenderItem<any> = useCallback((info) => {
    return <Item info={info} data={result} handleButton={handleButton} onDone={handleDone} colorScheme={colorScheme} />
  }, [colorScheme, handleButton, handleDone, result])

  const onScroll = useCallback((event) => {
    setIndex(Math.round((event.nativeEvent.contentOffset.x) / (dimension.width - 32)))
  }, [dimension.width])

  const keyExtractor = useCallback((item, index) => `${item.id || index}`, [])

  return (
    // <Screen style={ROOT} preset="scroll" colorScheme={colorScheme}>
      <ImageBackground source={pattern} style={IMAGE} resizeMode="repeat">
        <FlatList
          ref={FlatListRef}
          keyExtractor={keyExtractor}
          data={result}
          renderItem={renderItem}
          horizontal
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          nestedScrollEnabled
          estimatedItemSize={dimension.width - 32}
          scrollEventThrottle={1000}
          onScroll={onScroll}
        />
      </ImageBackground>
    // </Screen>
  )
})
